import type { TFunction } from 'i18next';
import { formatClock, formatDuration } from '@/features/trips/format';
import type { Alert } from './types';

/**
 * One alert row (canvas 1o's list, mobile 1e's read/unread affordance).
 *
 * PURPOSE SETS SEVERITY, NOT VISIBILITY (Mahad's ruling). Every crossing renders;
 * `severityOf(alert)` decides whether this row is the ALERT direction or the NORMAL
 * (status-event) one. The two look deliberately different — following the canvas's
 * own severity lever, the tag COLOUR:
 *   - alert · no_go   (entered a No-go zone)     → `--raad-error`  (critical)
 *   - alert · keep_in (left where it belongs)    → `--raad-warn`   (mid tier)
 *   - normal (returned / cleared / site visit)   → `--raad-slate`  (info)
 * The normal row must not shout: muted title, slate tag, slate unread dot. Tokens
 * only — no hex literal enters this file.
 *
 * THE HONEST STATES (PLAN §6) still live here, unchanged, and the distinction is a
 * whole point of its own:
 *   - `observed` → a real confirmed time. A fix bracketed the crossing.
 *   - `unobserved_gap` → NO crossing time. The row renders the BRACKET — last seen
 *     on the old side at X, first seen on the new side at Y, and the unobserved
 *     duration from `gapSec`. Never a single "crossed at" instant; the wire carries
 *     none to render.
 *
 * `read`/unread is shown as a leading dot and a weight change — no colour-only cue.
 */

/** The verb line. The normal (status) direction reads as a STATUS, not a warning:
 *  `returned` for a keep_in re-entry, `cleared` for leaving a no_go zone. The
 *  non-alerting site purposes (`location`, `poi`, slice 24) never carry the alert
 *  direction, so their crossings read as the plain enter/exit status verb. The
 *  alert direction keeps the plain crossing verb. */
function verbKey(alert: Alert): string {
  if (alert.severity === 'normal') {
    if (alert.purpose === 'keep_in') return 'alerts.event.returned';
    if (alert.purpose === 'no_go') return 'alerts.event.cleared';
    return alert.kind === 'exit' ? 'alerts.event.exit' : 'alerts.event.enter';
  }
  return alert.kind === 'exit' ? 'alerts.event.exit' : 'alerts.event.enter';
}

/** Tailwind token classes for the purpose tag, keyed by the three severity tiers. */
function tagClass(severity: 'alert' | 'normal', isNoGo: boolean): string {
  if (severity === 'normal') return 'bg-raad-slate/12 text-raad-slate';
  return isNoGo ? 'bg-raad-error/15 text-raad-error' : 'bg-raad-warn/18 text-raad-warn';
}

export function AlertRow({
  alert,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  alert: Alert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: (alert: Alert) => void;
  t: TFunction;
}) {
  const observedTime = formatClock(locale, Date.parse(alert.observedAt), timeZone);
  const priorTime =
    alert.priorAt != null ? formatClock(locale, Date.parse(alert.priorAt), timeZone) : null;
  const gapText = alert.gapSec != null ? formatDuration(t, alert.gapSec) : null;
  const isNoGo = alert.purpose === 'no_go';
  const severity = alert.severity;
  const isAlert = severity === 'alert';

  // The title's weight/tone. An ALERT direction carries the weight (bold, full
  // text) when unread; a NORMAL direction reads as a quiet status event in either
  // read state. Read alerts soften to match the normal tone.
  const titleClass =
    isAlert && !alert.read ? 'font-semibold text-raad-text' : 'font-medium text-raad-muted';
  // Unread dot: accent for an alert, slate for a status event so a "returned"/
  // "cleared" row never shouts (Mahad's ruling). Colour is a reinforcement, not the
  // sole cue — the leading dot + the title weight already carry read-state.
  const dotClass = alert.read ? 'bg-transparent' : isAlert ? 'bg-raad-accent' : 'bg-raad-slate';

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(alert)}
        aria-pressed={selected}
        data-severity={severity}
        data-alert-id={alert.id}
        className={[
          'w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
          selected
            ? 'border-raad-primary/40 bg-raad-primary/10'
            : isAlert
              ? 'border-raad-line-7 bg-raad-tile hover:border-raad-line-15'
              : 'border-raad-line-5 bg-raad-tile/60 hover:border-raad-line-12',
        ].join(' ')}
      >
        <div className="flex items-start gap-2.5">
          {/* Unread dot — leading, so it reads at a glance without relying on colour
              alone (the weight of the title also changes). */}
          <span aria-hidden="true" className={['mt-1.5 size-2 flex-none rounded-full', dotClass].join(' ')} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className={['truncate text-sm', titleClass].join(' ')}>
                {t(verbKey(alert), { name: alert.geofenceName })}
              </span>
              <span
                data-testid="alert-tag"
                className={[
                  'flex-none rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                  tagClass(severity, isNoGo),
                ].join(' ')}
              >
                {t(`zones.purpose.${alert.purpose}`)}
              </span>
              {!alert.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
            </div>

            {plate ? (
              <div className="mt-0.5 truncate text-[11.5px] font-semibold text-raad-dim">{plate}</div>
            ) : null}

            {/* The meta line — the honest state. */}
            {alert.certainty === 'observed' ? (
              <div className="mt-1 text-[12px] text-raad-muted">
                {t('alerts.observedAt', { time: observedTime })}
              </div>
            ) : (
              <div className="mt-1 space-y-0.5">
                <span className="inline-block rounded-md border border-raad-line-10 bg-raad-panel px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-raad-dim">
                  {t('alerts.gap.badge')}
                </span>
                <p className="text-[12px] leading-relaxed text-raad-muted">
                  {priorTime != null
                    ? t(alert.kind === 'exit' ? 'alerts.gap.exit' : 'alerts.gap.enter', {
                        prior: priorTime,
                        observed: observedTime,
                        gap: gapText ?? '',
                      })
                    : t('alerts.gap.noPrior', { observed: observedTime })}
                </p>
              </div>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}
